import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from '/logo.png';
import { theme } from '../theme';

// Animations
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SkipLink = styled.a`
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 999;
  padding: 1rem;
  background: ${theme.gradients.professional};
  color: white;
  font-weight: ${theme.typography.fontWeight.semibold};
  text-decoration: none;
  border-radius: 0 0 ${theme.borderRadius.md} ${theme.borderRadius.md};

  &:focus {
    left: 50%;
    transform: translateX(-50%);
    top: 0.5rem;
    box-shadow: ${theme.shadows.xl};
  }
`;

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: ${props => props.$scrolled ? '0.75rem 1rem' : '1rem'};
  background: ${props => props.$scrolled 
    ? 'rgba(255, 255, 255, 0.8)' 
    : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: ${props => props.$scrolled 
    ? theme.shadows.lg 
    : theme.shadows.sm};
  z-index: ${theme.zIndex.sticky};
  transition: all ${theme.transitions.base};
  border-bottom: 1px solid ${props => props.$scrolled 
    ? 'rgba(59, 130, 246, 0.1)' 
    : 'transparent'};

  @media (max-width: 768px) {
    padding: ${props => props.$scrolled ? '0.5rem 0.5rem' : '1rem 0.5rem'};
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeight.bold};
  font-family: ${theme.typography.fontFamily.heading};
  background: ${theme.gradients.professional};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
  cursor: pointer;
  transition: transform ${theme.transitions.base};

  &:hover {
    transform: scale(1.05);
  }

  img {
    height: 32px;
    width: auto;
    transition: transform ${theme.transitions.base};
    
    &:hover {
      transform: rotate(10deg);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    gap: 0.5rem;
    
    img {
      height: 28px;
    }
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    cursor: pointer;
    font-size: 1.5rem;
  }
`;

const MobileNav = styled.div`
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: ${theme.shadows.xl};
  flex-direction: column;
  padding: 1.5rem;
  z-index: ${theme.zIndex.dropdown};
  border-bottom: 2px solid ${theme.colors.primary[100]};
  animation: ${slideDown} 0.3s ease-out;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
  }
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.backdrop};

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.$active ? theme.colors.primary[600] : theme.colors.neutral[700]};
  text-decoration: none;
  font-weight: ${props => props.$active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  transition: all ${theme.transitions.base};
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background-color: ${props => props.$active 
    ? `${theme.colors.primary[500]}15` 
    : 'transparent'};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) scaleX(${props => props.$active ? '1' : '0'});
    width: 80%;
    height: 2px;
    background: ${theme.gradients.professional};
    transition: transform ${theme.transitions.base};
  }
  
  &:hover {
    color: ${theme.colors.primary[600]};
    background-color: ${theme.colors.primary[500]}10;
    
    &::after {
      transform: translateX(-50%) scaleX(1);
    }
  }

  &[data-active='true'] {
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.semibold};
    background-color: ${theme.colors.primary[500]}15;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    width: 100%;
    text-align: center;
    border-radius: ${theme.borderRadius.lg};
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }
`;

const MobileNavLink = styled(Link)`
  color: ${props => props.$active ? theme.colors.primary[600] : theme.colors.neutral[700]};
  text-decoration: none;
  font-weight: ${props => props.$active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium};
  transition: all ${theme.transitions.base};
  cursor: pointer;
  padding: 1rem 1.5rem;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  display: block;
  text-align: left;
  width: 100%;
  background-color: ${props => props.$active ? `${theme.colors.primary[500]}15` : 'transparent'};
  border: 2px solid ${props => props.$active ? theme.colors.primary[200] : 'transparent'};
  
  &:hover {
    color: ${theme.colors.primary[600]};
    background-color: ${theme.colors.primary[500]}10;
    border-color: ${theme.colors.primary[200]};
    transform: translateX(4px);
  }
  
  &[data-active='true'] {
    color: ${theme.colors.primary[600]};
    font-weight: ${theme.typography.fontWeight.semibold};
    background-color: ${theme.colors.primary[500]}15;
    border-color: ${theme.colors.primary[200]};
  }
`;

function Header() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const mobileNavRef = useRef(null);
  const isHomePage = location.pathname === '/';
  
  // Detect scroll position for header styling
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Update active section based on scroll position (including Impact Showcase)
  useEffect(() => {
    if (!isHomePage) return;
    
    const handleScroll = () => {
      const sections = ['hero', 'about', 'projects', 'impact-showcase', 'certificates', 'contact'];
      const scrollPosition = window.scrollY + 100; // Add offset for fixed header
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // Click outside to close mobile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && mobileNavRef.current && !mobileNavRef.current.contains(event.target)) {
        // Check if click is not on the menu toggle button
        const menuButton = event.target.closest('[data-mobile-menu-toggle]');
        if (!menuButton) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    // Close mobile menu after clicking a link
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <Nav $scrolled={scrolled}>
      <NavContainer>
        <Logo to="/" onClick={(e) => {
          if (isHomePage) {
            e.preventDefault();
            scrollToSection('hero');
          }
        }}>
            <img src={logo} alt="Kabil Mbarki Portfolio Logo" />
          Portfolio
        </Logo>
          <MobileMenu onClick={toggleMenu} data-mobile-menu-toggle>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenu>
        <NavLinks>
          {isHomePage ? (
            <>
              <NavLink 
                  as="button"
                onClick={() => scrollToSection('about')}
                $active={activeSection === 'about' ? 'true' : undefined}
                data-active={activeSection === 'about'}
              >
                About
              </NavLink>
              <NavLink 
                  as="button"
                onClick={() => scrollToSection('projects')}
                $active={activeSection === 'projects' ? 'true' : undefined}
                data-active={activeSection === 'projects'}
              >
                Projects
              </NavLink>
              <NavLink 
                  as="button"
                  onClick={() => scrollToSection('impact-showcase')}
                  $active={activeSection === 'impact-showcase' ? 'true' : undefined}
                  data-active={activeSection === 'impact-showcase'}
                >
                  Impact
                </NavLink>
                <NavLink 
                  as="button"
                onClick={() => scrollToSection('certificates')}
                $active={activeSection === 'certificates' ? 'true' : undefined}
                data-active={activeSection === 'certificates'}
              >
                Certificates
              </NavLink>
              <NavLink 
                  as="button"
                onClick={() => scrollToSection('contact')}
                $active={activeSection === 'contact' ? 'true' : undefined}
                data-active={activeSection === 'contact'}
              >
                Contact
              </NavLink>
            </>
          ) : (
            <NavLink to="/">Home</NavLink>
          )}
        </NavLinks>
        </NavContainer>
      </Nav>

      {/* Mobile Overlay */}
      <MobileOverlay $isOpen={isOpen} onClick={() => setIsOpen(false)} />

      {/* Mobile Navigation */}
      <MobileNav $isOpen={isOpen} ref={mobileNavRef}>
          {isHomePage ? (
            <>
              <MobileNavLink 
              as="button"
                onClick={() => scrollToSection('about')}
                $active={activeSection === 'about' ? 'true' : undefined}
                data-active={activeSection === 'about'}
              >
                About
              </MobileNavLink>
              <MobileNavLink 
              as="button"
                onClick={() => scrollToSection('projects')}
                $active={activeSection === 'projects' ? 'true' : undefined}
                data-active={activeSection === 'projects'}
              >
                Projects
              </MobileNavLink>
              <MobileNavLink 
              as="button"
              onClick={() => scrollToSection('impact-showcase')}
              $active={activeSection === 'impact-showcase' ? 'true' : undefined}
              data-active={activeSection === 'impact-showcase'}
            >
              Impact Showcase
            </MobileNavLink>
            <MobileNavLink 
              as="button"
                onClick={() => scrollToSection('certificates')}
                $active={activeSection === 'certificates' ? 'true' : undefined}
                data-active={activeSection === 'certificates'}
              >
                Certificates
              </MobileNavLink>
              <MobileNavLink 
              as="button"
                onClick={() => scrollToSection('contact')}
                $active={activeSection === 'contact' ? 'true' : undefined}
                data-active={activeSection === 'contact'}
              >
                Contact
              </MobileNavLink>
            </>
        ) : (
          <MobileNavLink to="/">
            Home
          </MobileNavLink>
        )}
        </MobileNav>
    </>
  );
}

export default Header;
